<template>
  <div v-if="activeContent" class="mt-2">
    <div class="customer-reaction__title px-4 color-primary3 uppercase font-size-[12px] font-bold">
      {{ t('canvasUI.notesApp.customerReaction') }}
    </div>

    <div class="customer-reaction__content px-4 pt-2 pb-4 gap-2 flex justify-between">
      <div class="flex gap-[6px] items-center break-anywhere">
        <CIcon
          v-if="
            activeContent.type === 'section' || activeContent.type === 'canvas' || activeContent.type === 'canvas_page'
          "
          fa-type="far"
          icon="presentation"
        />

        <CIcon v-else icon="file" />
        {{ title }}
      </div>

      <div class="flex gap-2 items-center">
        <div v-if="activePage" class="font-size-4 text-nowrap">
          {{ t('canvasUI.notesApp.page', { count: activePage }) }}
        </div>

        <CIcon
          class="font-size-6 cursor-pointer color-error"
          :class="getActiveClass(-1)"
          icon="face-frown"
          @click="setRating(-1)"
        />

        <CIcon
          class="font-size-6 cursor-pointer"
          :class="getActiveClass(0)"
          icon="face-meh-blank"
          @click="setRating(0)"
        />

        <CIcon
          class="font-size-6 cursor-pointer"
          :class="getActiveClass(1)"
          icon="face-grin-stars"
          @click="setRating(1)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import type { Call, ContentRating } from '@lib/types/call'
import { useLocalStorage } from '@vueuse/core'
import { CALL_STORAGE_KEY, INITIAL_CALL_STATE } from '@lib/types/call'
import usePresentationHistory from '@lib/composables/presentationHistory.use'
import { useI18n } from 'vue-i18n'
import type { TrackingEvent } from '@lib/constants/cdp.const'
import { CDP_EVENT_TYPE, TRACKING_EVENTS_STORAGE_KEY } from '@lib/constants/cdp.const'
import dayjs from 'dayjs'

const themeVars = useThemeVars()
const { t } = useI18n()
const { setMetadataItem } = usePresentationHistory()

const call = useLocalStorage<Call>(CALL_STORAGE_KEY, INITIAL_CALL_STATE, {
  deep: true,
  listenToStorageChanges: true,
})

const trackingEvents = useLocalStorage<TrackingEvent[]>(TRACKING_EVENTS_STORAGE_KEY, [], {
  deep: true,
  listenToStorageChanges: true,
})

const presentationHistoryMetadata = computed(() => call.value?.presentationHistoryMetadata)
const activeContent = computed(() => {
  let history = call.value?.presentationHistory
  if (!history?.length) return undefined

  const property = history.some((item) => 'lastTimeStarted' in item) ? 'lastTimeStarted' : 'timeStarted'
  history = history.filter((item) => property in item)
  return history.length
    ? history.reduce((latest, current) => ((current[property] || 0) > (latest[property] || 0) ? current : latest))
    : undefined
})
const activeContentId = computed(() => {
  if (!activeContent.value) return undefined

  const { id, page } = activeContent.value
  let result = id

  if (page !== undefined) {
    result += '/' + page
  }

  return result
})

const rating = computed<ContentRating | undefined>(() => {
  if (!activeContentId.value) return undefined

  return presentationHistoryMetadata.value?.[activeContentId.value]?.rating
})

const title = computed(() => activeContent.value?.name)

const activePage = computed(() => {
  const pageIndex = activeContent.value?.page

  if (!Number.isInteger(pageIndex)) {
    return undefined
  }

  return ((pageIndex as number) + 1).toString()
})

const getActiveClass = (_rating: ContentRating) => {
  if (rating.value === _rating) {
    if (_rating === -1) {
      return 'color-error'
    } else if (_rating === 0) {
      return 'color-primary3'
    }

    return 'color-success'
  }

  return 'color-primary4'
}

const addTrackingEvent = (body: any, state: ContentRating) => {
  trackingEvents.value.push({
    event_name: CDP_EVENT_TYPE.customerSentimentCaptured,
    payload: {
      id: body.id,
      page: body?.page,
      name: body?.name,
      type: body?.type,
      local_timestamp: dayjs().format(),
      rating: state,
    },
  })
}

const setRating = (state: ContentRating) => {
  if (!activeContentId.value) {
    return
  }

  addTrackingEvent(activeContent.value, state)
  setMetadataItem(call, activeContentId.value, state)
}
</script>

<style scoped lang="scss">
.customer-reaction {
  &__title {
    letter-spacing: 0.5px;
  }

  &__content {
    border-top: 1px solid v-bind('themeVars.primary6');
  }
}
</style>
